import {
  Column,
  Columns,
  EmptyState,
  FlexBox,
  HelpIcon,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { AnalyticsMenu, type AnalyticsMenuActions } from 'lib-analytics-ui';
import { ampli } from 'lib-shared-tracking';

import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { analyticsActions } from '../../analytics.actions';
import { AnalyticsControls } from '../../components/controls/analyticsControls';
import { AnalyticsHeader } from '../../components/header/analyticsHeader';
import { AnalyticsPanels } from '../../components/panels/analyticsPanels';
import { AnalyticsQuickViewFavoriteToggle } from '../../components/quickView/components/favorite/quickViewFavoriteToggle';
import { AnalyticsQuickViewModal } from '../../components/quickView/components/modal/quickViewModal';

import { useQuickViewActions } from './utils/useQuickViewActions/useQuickViewActions';
import { useQuickViewInit } from './utils/useQuickViewInit/useQuickViewInit';

export const QuickViewView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { user } = useCurrentUser();

  const { quickView, asyncStatus } = useQuickViewInit();
  const {
    handleUpdateQuickView,
    handleDuplicateQuickView,
    handleDeleteQuickView,
    handleUpdateQuickViewFavoriteState,
  } = useQuickViewActions({ location: 'quickView' });

  const dashboard = useAppSelector((state) => state.analytics.dashboard);
  const meta = useAppSelector((state) => state.analytics.meta);

  /* The relative time of the last time the quick view data was refreshed. */
  const lastRefreshTime = useShortHumanReadableTimer(meta.lastRefresh);

  /** Whether the user can modify the current quick view. */
  const canModify = quickView?.user.id === user.id;

  /* The list of actions available for the current quick view. */
  const actions: AnalyticsMenuActions = [
    {
      type: 'button',
      icon: 'pen',
      label: 'Edit view',
      disabled: !canModify,
      tooltip: !canModify
        ? 'You can only edit quick views that are created by you.'
        : undefined,
      onClick: () => quickView && handleUpdateQuickView(quickView),
    },
    {
      type: 'button',
      icon: 'copy',
      label: 'Duplicate view',
      onClick: () => quickView && handleDuplicateQuickView(quickView),
    },
    {
      type: 'divider',
    },
    {
      type: 'button',
      icon: 'trash',
      label: 'Delete view',
      disabled: !canModify,
      tooltip: !canModify
        ? 'You can only delete quick views that are created by you.'
        : undefined,
      onClick: async () => {
        if (quickView) {
          const { isDone } = await handleDeleteQuickView(quickView);
          isDone && navigate({ path: '/analytics/quick-views' });
        }
      },
    },
    {
      type: 'divider',
    },
    {
      type: 'button',
      icon: 'redo',
      label: 'Refresh data',
      description: `Last refreshed ${lastRefreshTime}`,
      disabled: false,
      onClick: () => {
        dispatch(
          analyticsActions.setMeta({
            meta: { lastRefresh: new Date().toISOString() },
          }),
        );

        if (dashboard) {
          ampli.analyticsRefreshClicked({ dashboard: dashboard.title });
        }
      },
    },
  ];

  return (
    <Columns>
      <Column>
        <Column.Row slottable borderBottom>
          <Column.Slot>
            <AnalyticsHeader>
              {quickView && (
                <>
                  <Text.Heading color="primary" as="h2">
                    {quickView.name}
                  </Text.Heading>

                  {quickView.description && (
                    <HelpIcon text={quickView.description} dark />
                  )}

                  <Tooltip
                    content={
                      quickView.favorited
                        ? 'Remove from the sidebar'
                        : 'Add to the sidebar'
                    }
                    dark
                  >
                    <AnalyticsQuickViewFavoriteToggle
                      favorited={quickView.favorited}
                      onClick={() =>
                        handleUpdateQuickViewFavoriteState(quickView)
                      }
                    />
                  </Tooltip>
                </>
              )}
            </AnalyticsHeader>
          </Column.Slot>

          <Column.Slot noVerticalPadding placement="end">
            <AnalyticsMenu actions={actions} direction="left" />
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} scrollable>
          {(asyncStatus === 'uninitialized' || asyncStatus === 'pending') && (
            <FlexBox height="100%" alignItems="center" justifyContent="center">
              <Spinner />
            </FlexBox>
          )}

          {asyncStatus === 'rejected' && (
            <EmptyState
              title="Something went wrong"
              description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
              icon="exclamation"
              iconColor="red.500"
            />
          )}

          {asyncStatus === 'fulfilled' && (
            <>
              <FlexBox flexDirection="column" gap="400">
                <AnalyticsControls />
                <AnalyticsPanels />
              </FlexBox>

              <AnalyticsQuickViewModal />
            </>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
